import React, { useState } from "react";
import { useNotification } from "../../contexts/notification/NotificationContext";
import NotificationItem from "../../components/common/Notifications/NotificationItem";
import {
  Filter,
  Check,
  Loader,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
} from "lucide-react";
import { Toaster } from "react-hot-toast";

// Type options and read status options remain the same
const typeOptions = [
  { value: "", label: "All Types" },
  { value: "system", label: "System" },
  { value: "watchlist", label: "Watchlist" },
  { value: "kyc", label: "KYC" },
  { value: "portfolio", label: "Portfolio" },
  { value: "appointment", label: "Appointment" },
  { value: "wallet", label: "Wallet" },
  // { value: "Holdings", label: "Holdings" },
  { value: "general", label: "General" },
];

const readStatusOptions = [
  { value: "", label: "All Status" },
  { value: "false", label: "Unread" },
  { value: "true", label: "Read" },
];

// Pagination component with enhanced styling
const Pagination = ({ currentPage, totalPages, onPageChange }) => {
  // Function to generate page numbers with ellipsis for large page counts
  const getPageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 5;

    if (totalPages <= maxPagesToShow) {
      // Show all pages if total is less than max to show
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      // Calculate start and end of page numbers to show
      let startPage = Math.max(2, currentPage - 1);
      let endPage = Math.min(totalPages - 1, currentPage + 1);

      // Adjust if at edges
      if (currentPage <= 2) {
        endPage = Math.min(totalPages - 1, 4);
      } else if (currentPage >= totalPages - 1) {
        startPage = Math.max(2, totalPages - 3);
      }

      // Add ellipsis before middle pages if needed
      if (startPage > 2) {
        pages.push("...");
      }

      // Add middle pages
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }

      // Add ellipsis after middle pages if needed
      if (endPage < totalPages - 1) {
        pages.push("...");
      }

      // Always show last page
      pages.push(totalPages);
    }

    return pages;
  };

  return (
    <div className="flex items-center space-x-1">
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className={`p-2 rounded-md transition-all duration-200 ${
          currentPage === 1
            ? "text-gray-300 cursor-not-allowed"
            : "text-indigo-600 hover:bg-indigo-50"
        }`}
        aria-label="Previous page"
      >
        <ChevronLeft size={18} />
      </button>

      {getPageNumbers().map((page, index) => (
        <React.Fragment key={index}>
          {page === "..." ? (
            <span className="px-3 py-1 text-gray-500 font-medium">...</span>
          ) : (
            <button
              onClick={() => onPageChange(page)}
              className={`px-3 py-1 rounded-md text-sm font-medium transition-all duration-200 ${
                currentPage === page
                  ? "bg-indigo-600 text-white shadow-md"
                  : "text-gray-700 hover:bg-indigo-50 hover:text-indigo-700"
              }`}
              aria-label={`Page ${page}`}
              aria-current={currentPage === page ? "page" : undefined}
            >
              {page}
            </button>
          )}
        </React.Fragment>
      ))}

      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className={`p-2 rounded-md transition-all duration-200 ${
          currentPage === totalPages
            ? "text-gray-300 cursor-not-allowed"
            : "text-indigo-600 hover:bg-indigo-50"
        }`}
        aria-label="Next page"
      >
        <ChevronRight size={18} />
      </button>
    </div>
  );
};

const NotificationsPage = () => {
  const {
    notifications,
    loading,
    error,
    pagination,
    filters,
    updateFilters,
    setPage,
    markAllAsRead,
    fetchNotifications,
  } = useNotification();

  const [isFilterVisible, setIsFilterVisible] = useState(false);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    updateFilters({ [name]: value === "" ? null : value });
  };

  const handleRefresh = () => {
    fetchNotifications();
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const toggleFilters = () => {
    setIsFilterVisible(!isFilterVisible);
  };

  return (
    <div className="p-6 max-w-6xl mx-auto bg-gray-50 min-h-screen mt-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 tracking-tight mb-1">
          Notifications
        </h1>
        <p className="text-sm text-gray-500 font-normal">
          Stay updated with all your investment activities and important alerts
        </p>
      </div>

      <div className="flex flex-wrap justify-between items-center mb-6 gap-4">
        <div className="flex flex-wrap gap-3">
          <button
            className="inline-flex items-center px-4 py-2.5 border border-gray-200 rounded-lg shadow-sm bg-white text-gray-800 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all font-medium text-sm"
            onClick={toggleFilters}
          >
            <Filter size={16} className="mr-2 text-indigo-500" />
            <span>Filter</span>
          </button>

          <button
            className="inline-flex items-center px-4 py-2.5 border border-gray-200 rounded-lg shadow-sm bg-white text-gray-800 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all font-medium text-sm"
            onClick={handleRefresh}
          >
            <RefreshCw size={16} className="mr-2 text-indigo-500" />
            <span>Refresh</span>
          </button>
        </div>

        <button
          className="inline-flex items-center px-5 py-2.5 border border-transparent rounded-lg shadow-md bg-indigo-600 text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all font-medium text-sm"
          onClick={markAllAsRead}
        >
          <Check size={16} className="mr-2" />
          <span>Mark All as Read</span>
        </button>
      </div>

      {isFilterVisible && (
        <div className="bg-white p-6 rounded-xl mb-6 shadow-md border border-gray-100">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            Filter Notifications
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex flex-col">
              <label
                htmlFor="type-filter"
                className="text-sm font-medium text-gray-700 mb-2"
              >
                Notification Type
              </label>
              <select
                id="type-filter"
                name="type"
                value={filters.type || ""}
                onChange={handleFilterChange}
                className="block w-full pl-4 pr-10 py-3 text-base border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 rounded-lg shadow-sm"
              >
                {typeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col">
              <label
                htmlFor="read-filter"
                className="text-sm font-medium text-gray-700 mb-2"
              >
                Read Status
              </label>
              <select
                id="read-filter"
                name="isRead"
                value={filters.isRead === null ? "" : filters.isRead.toString()}
                onChange={handleFilterChange}
                className="block w-full pl-4 pr-10 py-3 text-base border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 rounded-lg shadow-sm"
              >
                {readStatusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="flex flex-col items-center justify-center p-12">
            <Loader className="animate-spin text-indigo-500 mb-4" size={36} />
            <p className="text-gray-600 font-medium">
              Loading your notifications...
            </p>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center p-12 text-center">
            <div className="bg-red-100 rounded-full p-3 mb-4">
              <AlertCircle size={28} className="text-red-500" />
            </div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">
              Unable to load notifications
            </h3>
            <p className="text-gray-600 mb-4 max-w-md">
              We encountered an error while loading your notifications. Please
              try again later.
            </p>
            <button
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm bg-indigo-600 text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all font-medium text-sm"
              onClick={handleRefresh}
            >
              Try Again
            </button>
          </div>
        ) : notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-12 text-center">
            <div className="w-40 h-40 mb-6"></div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">
              No notifications found
            </h3>
            <p className="text-gray-600 max-w-md">
              You don&apos;t have any notifications matching your current
              filters. Try adjusting your filters or check back later.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {notifications.map((notification) => (
              <NotificationItem
                key={notification._id}
                notification={notification}
              />
            ))}
          </div>
        )}
      </div>

      {!loading && notifications.length > 0 && (
        <div className="mt-8 flex flex-col sm:flex-row justify-between items-center gap-4">
          <Pagination
            currentPage={pagination.page}
            totalPages={pagination.pages}
            onPageChange={handlePageChange}
          />
          <div className="text-sm text-gray-600 font-medium">
            Showing{" "}
            <span className="font-semibold text-gray-800">
              {(pagination.page - 1) * pagination.limit + 1}
            </span>{" "}
            to{" "}
            <span className="font-semibold text-gray-800">
              {Math.min(pagination.page * pagination.limit, pagination.total)}
            </span>{" "}
            of{" "}
            <span className="font-semibold text-gray-800">
              {pagination.total}
            </span>{" "}
            notifications
          </div>
        </div>
      )}
      <Toaster
        position="top-right"
        reverseOrder={false}
        gutter={8}
        containerClassName=""
        containerStyle={{}}
        toastOptions={{
          // Global toast options
          duration: 4000,
          style: {
            background: "#fff",
            color: "#363636",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
            borderRadius: "8px",
            padding: "16px",
            fontSize: "14px",
            fontWeight: "500",
          },

          // Success toast styling
          success: {
            duration: 3000,
            style: {
              background: "#10B981",
              color: "#fff",
            },
            iconTheme: {
              primary: "#fff",
              secondary: "#10B981",
            },
          },

          // Error toast styling
          error: {
            duration: 4000,
            style: {
              background: "#EF4444",
              color: "#fff",
            },
            iconTheme: {
              primary: "#fff",
              secondary: "#EF4444",
            },
          },
        }}
      />
    </div>
  );
};

export default NotificationsPage;
